import React from 'react';
import { ControlledDateTimePickerContext } from '../../../ControlledDateTimePickerCTX.js';
import type { DSControlledDateTimePickerT } from '../../../react-desc-prop-types.js';
import { compareTwoDatesDayEquality, getDayFromDateString, isSameDateMoment } from '../../../utils/dateHelpers.js';
import { CalendarContext } from './CalendarContext.js';

export const useGetDayFlags = ({
  metaDay,
  isFirstCurrMonthFallback,
}: {
  metaDay: DSControlledDateTimePickerT.MetaMonthDay;
  isFirstCurrMonthFallback: boolean;
}) => {
  const { day } = metaDay;
  const {
    dateStringFromProps,
    getIsDisabledDay,
    getIsOutOfRangeDay,
    getIsStartRangeDay,
    getIsDayInRange,
    getIsEndRangeDay,
    latestInteractionRegion,
  } = React.useContext(ControlledDateTimePickerContext);

  const { focusedDay } = React.useContext(CalendarContext);
  const isFocusedMetaDay = focusedDay?.day ? compareTwoDatesDayEquality(day, focusedDay?.day) : false;
  const isSelected = React.useMemo(() => {
    const selectedDate = getDayFromDateString(dateStringFromProps);
    return selectedDate ? isSameDateMoment(day, selectedDate) : false;
  }, [dateStringFromProps, day]);
  const isFocused = latestInteractionRegion === 'calendar-days' && (isFocusedMetaDay || isFirstCurrMonthFallback);
  const isDisabled = getIsDisabledDay(metaDay.dayAsString);
  const isOutOfRange = getIsOutOfRangeDay(metaDay.dayAsString);
  const isStartRangeDay = getIsStartRangeDay(metaDay.dayAsString);
  const isEndRangeDay = getIsEndRangeDay(metaDay.dayAsString);
  const isDayInRangeImproper = getIsDayInRange(metaDay.dayAsString);
  const isDayInRange = !isStartRangeDay && !isEndRangeDay && isDayInRangeImproper;

  return React.useMemo(
    () => ({
      isSelected,
      isFocused,
      isDisabled,
      isOutOfRange,
      isStartRangeDay,
      isEndRangeDay,
      isDayInRangeImproper,
      isDayInRange,
    }),
    [
      isSelected,
      isFocused,
      isDisabled,
      isOutOfRange,
      isStartRangeDay,
      isEndRangeDay,
      isDayInRangeImproper,
      isDayInRange,
    ],
  );
};
